'use client'

import { useEffect } from 'react'

import { Notifications } from '@mantine/notifications'
import { QueryClientProvider } from '@tanstack/react-query'
import type { AbstractIntlMessages } from 'next-intl'
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useSearchParams } from 'next/navigation'

import * as config from '@/constants/config'
import { initializeFaroClient } from '@/lib/grafana/faro'
import { LocaleProvider } from '@/lib/locale'
import { LOCALE_PARAM_KEY } from '@/lib/locale/locale.constants'
import { type Locale } from '@/lib/locale/locale.types'
import { getQueryClient } from '@/lib/query-client'
import { ThemePreferences } from '@/types/platform'
import { clearLegacyChartDefinitionCookies } from '@/utils/cookies'
import { extractSubdomain } from '@/utils/subdomain'

import AmplifyProvider from './amplify'
import MantineProvider from './mantine'

type ProvidersProps = Readonly<{
  children: React.ReactNode
  initialTheme: ThemePreferences
  initialLocale: Locale
  initialMessages: AbstractIntlMessages
}>

const Providers = (props: ProvidersProps) => {
  const { children, initialTheme, initialLocale, initialMessages } = props

  useEffect(() => {
    initializeFaroClient().catch((error) => {
      console.error('Failed to initialize Faro', error)
    })
  }, [])

  // TEMPORARY: remove after legacy chartdef cookies are no longer present in the wild
  // (old embedded chart HTML wrote chartdef_* / lastChartDefinitionId; shrinks Cookie header)
  useEffect(() => {
    clearLegacyChartDefinitionCookies()
  }, [])

  // The server resolved the cookie (or the default); a `?locale=` override wins, and
  // then the server's messages are for another locale, so they are not passed on.
  const searchParams = useSearchParams()
  const paramLocale = (searchParams.get(LOCALE_PARAM_KEY) as Locale) || undefined
  const locale = paramLocale ?? initialLocale
  const messages = locale === initialLocale ? initialMessages : undefined

  const subdomain = extractSubdomain(globalThis.location?.hostname || '') || undefined
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider initialTheme={initialTheme}>
        <Notifications
          limit={config.NOTIFICATIONS_LIMIT}
          position={config.NOTIFICATIONS_POSITION}
          zIndex={config.NOTIFICATIONS_Z_INDEX}
        />
        {/* Locale wraps Amplify so Amplify's loading state can use translations. */}
        <LocaleProvider initialLocale={locale} initialMessages={messages}>
          <AmplifyProvider subdomain={subdomain}>{children}</AmplifyProvider>
        </LocaleProvider>
      </MantineProvider>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  )
}

export default Providers
